import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { formatLongDate } from '@unifemga/shared';
import { api } from '@/lib/api';
import { money } from '@/lib/utils';
import {
  Alert,
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  Skeleton,
  Stat,
} from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { LOAN_STATUS } from '@/lib/status';

interface Exposure {
  totalExposureFormatted: string;
  activeGuaranteeCount: number;
  guarantees: Array<{
    loanId: string;
    loanNumber: string;
    borrowerName: string;
    productName: string;
    principal: string;
    outstanding: string;
    status: string;
    daysInArrears: number;
    confirmedAt: string | null;
  }>;
}

/**
 * What this member is guaranteeing — read-only.
 *
 * There is nothing to accept or decline here. A guarantee is agreed on the
 * telephone with a UNIFEMGA officer, and most guarantors have no account at
 * all; this page exists only for the minority who do, so they can see what they
 * are standing behind.
 */
export default function GuaranteedLoans() {
  const { data, isLoading } = useQuery({
    queryKey: ['guarantor-exposure'],
    queryFn: () => api.get<Exposure>('/guarantors/my-exposure'),
  });

  if (isLoading) return <Skeleton className="h-96" />;

  const guarantees = data?.guarantees ?? [];
  const inArrears = guarantees.filter((guarantee) => guarantee.daysInArrears > 0);

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="Standing for others"
        title="Loans you guarantee"
        description="What you are currently standing behind for fellow members."
      />

      {guarantees.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck size={22} />}
          title="You are not guaranteeing anything"
          description="If you agree to guarantee someone's loan, a UNIFEMGA officer will telephone you to confirm, and it will appear here once the loan is approved."
        />
      ) : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2">
            <Stat
              label="Total exposure"
              value={data?.totalExposureFormatted ?? '—'}
              hint="Outstanding on loans you guarantee"
              tone={inArrears.length > 0 ? 'warning' : 'neutral'}
              icon={<ShieldCheck size={16} />}
            />
            <Stat
              label="Loans guaranteed"
              value={data?.activeGuaranteeCount ?? 0}
              hint={inArrears.length > 0 ? `${inArrears.length} in arrears` : 'All on schedule'}
              tone={inArrears.length > 0 ? 'danger' : 'success'}
            />
          </div>

          {inArrears.length > 0 && (
            <Alert tone="warning" title="A loan you guarantee is behind" className="mb-6">
              <span className="inline-flex items-start gap-1.5">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                If it is not brought up to date, UNIFEMGA may ask you for the outstanding balance.
                The amount is the principal only — no interest or penalty is ever added.
              </span>
            </Alert>
          )}

          <div className="space-y-3">
            {guarantees.map((guarantee) => (
              <Card key={guarantee.loanId}>
                <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
                  <div className="min-w-0">
                    <p className="numeric font-heading font-bold text-foreground">
                      {guarantee.loanNumber}
                    </p>
                    <p className="text-sm text-foreground-secondary">
                      {guarantee.borrowerName} · {guarantee.productName}
                    </p>
                    {guarantee.confirmedAt && (
                      <p className="mt-1 text-xs text-foreground-secondary">
                        You confirmed on {formatLongDate(new Date(guarantee.confirmedAt))}
                      </p>
                    )}
                  </div>

                  <div className="text-right">
                    <p
                      className={
                        guarantee.daysInArrears > 0
                          ? 'display-figure text-lg text-danger'
                          : 'display-figure text-lg text-foreground'
                      }
                    >
                      {money(guarantee.outstanding)}
                    </p>
                    <div className="mt-1.5 flex items-center justify-end gap-2">
                      <StatusBadge map={LOAN_STATUS} status={guarantee.status} />
                    </div>
                    {guarantee.daysInArrears > 0 && (
                      <p className="mt-1 text-xs font-semibold text-danger">
                        {guarantee.daysInArrears} days in arrears
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      <p className="mt-6 text-center text-sm leading-relaxed text-foreground-secondary">
        Guarantees are agreed by telephone with a UNIFEMGA officer, so there is nothing to accept
        here. Questions? Call{' '}
        <a href="tel:+2349168484980" className="font-bold text-primary hover:underline">
          (+234) 916 848 4980
        </a>{' '}
        or see your{' '}
        <Link to="/portal/profile" className="font-bold text-primary hover:underline">
          profile
        </Link>
        .
      </p>
    </div>
  );
}
